import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, AlertCircle, Eye, EyeOff, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { profileService } from "@/services/profileService";

/**
 * Complete invitation page for invited users
 * - Processes invite links and establishes session
 * - Allows users to set their password
 * - Creates profile via ensure-profile edge function
 * - Redirects to dashboard on success
 */
export default function CompleteInvitePage() {
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");

  const ensureProfileAndRedirect = async () => {
    try {
      console.log("🔄 Ensuring profile exists...");
      
      // Use the centralized profile service
      const result = await profileService.ensureProfileWithFallback();
      
      if (result.success) {
        console.log("✅ Profile ensured, redirecting to dashboard...");
        toast.success("Welcome! You've been successfully added as a superintendent.");
        setTimeout(() => {
          window.location.replace("/");
        }, 1000);
      } else {
        console.error("❌ Profile creation failed:", result.error);
        setError(result.error || "Profile setup failed. Please contact support.");
      }
    } catch (error) {
      console.error("❌ Error ensuring profile:", error);
      setError("Profile setup failed. Please contact support.");
    }
  };

  useEffect(() => {
    const processInvite = async () => {
      try {
        console.log("🚀 Processing invite link...");
        
        // Check if user is already logged in and has password set
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (session?.user && !sessionError) {
          console.log("✅ User already authenticated, checking if password needed...");
          // Check if user already has password set (for returning users)
          const { data: user } = await supabase.auth.getUser();
          if (user?.user && user.user.email_confirmed_at) {
            console.log("✅ User already confirmed, redirecting to dashboard...");
            await ensureProfileAndRedirect();
            return;
          }
        }
        
        // First try PKCE code exchange
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        
        if (code) {
          console.log("✅ Found PKCE code, exchanging for session...");
          const { data, error: codeErr } = await supabase.auth.exchangeCodeForSession(window.location.href);
          
          if (!codeErr && data.session) {
            console.log("✅ PKCE session established");
            const user = data.user;
            if (user?.email) {
              setUserEmail(user.email);
              // For invited users, they need to set password
              console.log("🔑 User needs to set password");
              setNeedsPassword(true);
              setIsProcessing(false);
            return;
            }
          }
          
          console.warn("⚠️ PKCE exchange failed:", codeErr);
        }

        // Fallback: Try hash-based tokens
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        
        if (accessToken) {
          console.log("✅ Found hash tokens, setting session...");
          const { data, error: sessionErr } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken ?? "",
          });
          
          if (!sessionErr && data.session && data.user?.email) {
            console.log("✅ Hash session established");
            setUserEmail(data.user.email);
            // For invited users, they typically need to set password
            setNeedsPassword(true);
            setIsProcessing(false);
            return;
          }
          
          console.warn("⚠️ Hash session failed:", sessionErr);
        }

        // If we get here, no valid auth method was found
        console.error("❌ No valid authentication method found");
        setError("Invalid or expired invite link. Please contact your administrator.");
        
      } catch (e: any) {
        console.error("❌ Error processing invite:", e);
        setError("Failed to process invite link. Please try again.");
      } finally {
        setIsProcessing(false);
      }
    };
    

    
    processInvite();
  }, []);

  const handleTryAgain = () => {
    setIsProcessing(true);
    setError(null);
    window.location.reload();
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password) {
      toast.error("Please enter a password");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setIsSettingPassword(true);
    
    try {
      console.log("🔑 Setting user password...");
      
      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });
      
      if (updateError) {
        console.error("❌ Password update failed:", updateError);
        toast.error("Failed to set password. Please try again.");
        return;
      }
      
      console.log("✅ Password set successfully");
      toast.success("Password set successfully! Completing setup...");
      
      // Small delay to ensure password is updated
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Now complete the profile setup and redirect
      await ensureProfileAndRedirect();
      
    } catch (error) {
      console.error("❌ Error setting password:", error);
      toast.error("Failed to set password. Please try again.");
    } finally {
      setIsSettingPassword(false);
    }
  };

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-lg font-medium">Processing your invitation…</p>
            <p className="text-sm text-muted-foreground mt-2">Please wait while we verify your invite link</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/20">
              <CheckCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <CardTitle className="text-xl">Complete Your Setup</CardTitle>
            <CardDescription>
              Welcome! Please set a password to complete your account setup.
              {userEmail && (
                <span className="block text-sm font-medium text-blue-600 dark:text-blue-400 mt-2">
                  {userEmail}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    disabled={isSettingPassword}
                    required
                    minLength={6}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSettingPassword}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Password must be at least 6 characters long
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSettingPassword}
                  required
                  minLength={6}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSettingPassword || !password || !confirmPassword}
              >
                {isSettingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Setting Password...
                  </>
                ) : (
                  "Complete Setup"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-xl text-red-900 dark:text-red-100">Sign In Failed</CardTitle>
            <CardDescription className="text-red-700 dark:text-red-300">
              {error}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={handleTryAgain} variant="outline" className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}